import { createContext, useContext, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { Course, GrowthContent } from '@mc/api'
import { useStaticData } from '@mc/static-data-react'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'

const TrailerModalContext = createContext<{
  openVideoModal: (type: 'trailer' | 'sample', course?: CourseToPlay) => void
  closeVideoModal: () => void
}>({
  openVideoModal: () => {},
  closeVideoModal: () => {},
})

// Copied from pages/classes/[slug]/components/Hero/Hero.tsx
// Handles trailer props for both Course (main CM page) and GrowthContent (timeline similar courses)
const getTrailerProps = (course: CourseToPlay) => ({
  id: 'id' in course ? course.id : course.uuid,
  slug: course.slug,
  title: course.title,
  headline: course.headline,
  instructorName: course.instructorName,
  instructorAvatar: course.primary1x1,
  brightcoveVideoId:
    'brightcoveVideoId' in course ? course.brightcoveVideoId : course.mediaUuid,
  mediaUuid: course.mediaUuid,
})

// Copied from pages/classes/[slug]/components/Hero/Hero.tsx
const getSampleProps = (course: Course) => ({
  id: course.id || course.uuid,
  title: course.title,
  slug: course.slug,
  headline: course.headline,
  instructorName: course.instructorName,
  instructorAvatar: course.primary1x1,
  brightcoveVideoId: course?.sample?.brightcoveVideoId,
  mediaUuid: course?.sample?.mediaUuid,
})

const VideoModal = dynamic(() => import('components/VideoModal'), {
  ssr: false,
})

type CourseToPlay = Course | GrowthContent

export const VideoModalProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [videoModalType, setVideoModalType] = useState<
    'trailer' | 'sample' | undefined
  >(undefined)
  const { course: CMCourse } = useStaticData<{ course: Course }>()
  const [courseToPlay, setCourseToPlay] = useState<CourseToPlay>(CMCourse)

  const openVideoModal = (
    type: 'trailer' | 'sample',
    course?: CourseToPlay,
  ) => {
    setVideoModalType(type)
    setShowVideoModal(true)
    setCourseToPlay(course ?? CMCourse)
  }

  const closeVideoModal = () => {
    setShowVideoModal(false)
    setVideoModalType(undefined)
    setCourseToPlay(CMCourse)
  }

  const videoModalProps = useMemo(() => {
    if (videoModalType === 'trailer') {
      const trailer = getTrailerProps(courseToPlay)

      return {
        videoId: trailer.brightcoveVideoId,
        mediaUuid: trailer.mediaUuid || '',
        author: trailer.instructorName,
        tagline: trailer.headline,
        contentId: `${trailer.id}_${
          trailer.brightcoveVideoId ?? trailer.mediaUuid
        }`,
        contentTitle: trailer.title || '',
        contentType: TRACK_CONTENT_TYPES.COURSE_TRAILER,
        courseId: trailer.id || 0,
        instructorAvatar: trailer.instructorAvatar,
      }
    }

    const sample = getSampleProps(CMCourse)
    return {
      isSample: true,
      videoId: sample.brightcoveVideoId,
      mediaUuid: sample.mediaUuid ?? '',
      author: sample.instructorName ?? undefined,
      tagline: sample.headline ?? undefined,
      instructorAvatar: sample.instructorAvatar ?? undefined,
      contentId: `${sample.id}_${sample.brightcoveVideoId ?? sample.mediaUuid}`,
      contentTitle: sample.title ?? '',
      contentType: TRACK_CONTENT_TYPES.COURSE_SAMPLE,
      courseId: sample.id || 0,
    }
  }, [videoModalType, courseToPlay, CMCourse])

  return (
    <TrailerModalContext.Provider value={{ openVideoModal, closeVideoModal }}>
      {children}

      {showVideoModal && (
        <VideoModal show onCloseModal={closeVideoModal} {...videoModalProps} />
      )}
    </TrailerModalContext.Provider>
  )
}

export const useVideoModal = () => {
  const context = useContext(TrailerModalContext)
  if (!context) {
    throw new Error('useVideoModal must be used within a VideoModalProvider')
  }
  return context
}
